import Decimal from 'decimal.js';

import { ONE_MINUTE } from 'constants/charting';
import type { HistoricalDataPeriodInterval, MarketCandlestickVo } from 'models/historical_data';
import type { HistoricalMarketStats } from 'models/stats';

const periodIntervalToSeconds = (periodInterval: HistoricalDataPeriodInterval): number => {
    const parsed = parseInt(periodInterval, 10);
    return parsed === 0 ? 5 : parsed * ONE_MINUTE;
};

/** /v1 counts both sides; match /trade-api/v2's truncation to one-sided centicounts. */
export const candlestickCountContracts = (fixedPoint: string | undefined, legacy: number): Decimal =>
    new Decimal(fixedPoint !== undefined && fixedPoint !== '' ? fixedPoint : legacy)
        .div(2)
        .toDecimalPlaces(2, Decimal.ROUND_DOWN);

/** Server-safe pre-extrapolation history, with exact one-sided contract counts. */
export const mapCandlesticksToStatsHistory = (
    candlesticks: MarketCandlestickVo[],
    periodInterval: HistoricalDataPeriodInterval,
): HistoricalMarketStats[] => {
    let accumulatedVolume = new Decimal(0);
    let lastValidPrice = 0;
    const intervalSeconds = periodIntervalToSeconds(periodInterval);

    return candlesticks.map((candlestick) => {
        accumulatedVolume = accumulatedVolume.plus(
            candlestickCountContracts(candlestick.volume_fp, candlestick.volume),
        );
        lastValidPrice =
            candlestick.price.close > 0
                ? candlestick.price.close
                : !!candlestick.price.previous && candlestick.price.previous > 0
                  ? candlestick.price.previous
                  : lastValidPrice;
        return {
            yesAsk: candlestick.yes_ask.close || candlestick.yes_ask.previous || 0,
            yesBid: candlestick.yes_bid.close || candlestick.yes_bid.previous || 0,
            price: lastValidPrice,
            volume: accumulatedVolume.toNumber(),
            openInterest: candlestickCountContracts(candlestick.open_interest_fp, candlestick.open_interest).toNumber(),
            ts: candlestick.end_period_ts - intervalSeconds,
        };
    });
};

export const emptyStatsHistory = (now: number = Math.round(Date.now() / 1000)): HistoricalMarketStats[] => [
    { yesAsk: 50, yesBid: 50, price: 0, volume: 0, openInterest: 0, ts: now },
];
